import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppService } from '@irb/app.service';
import { PageService } from '@irb-common/services/page.service';
import { IrbDashboardService } from '@irb/irb-dashboard/irb-dashboard.service';
import { ITopModelDetails } from '@irb/irb-dashboard/irb-dashboard.model';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { DataTransferService } from '@irb/irb-common/services/data-transfer.service';
import { IApplyFilterOnLanding } from '@irb/app.model';
import { AnalyticsService } from '@irb/irb-common/analytics/analytics.service';

@Component({ standalone: false,
  selector: 'irb-com-models',
  templateUrl: './com-models.component.html',
  styleUrls: ['./com-models.component.scss']
})
export class ComModelsComponent implements OnInit {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private _data: ITopModelDetails;
  chartData: any;
  faChevronRight = faChevronRight;

  colors: Array<string> = [
    "#197804",
    "#2A5673",
    "#64A1C6",
    "#B2D7ED",
    "#E1EEF9",
    "#F7D461"
  ];

  options: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: Math.PI,
    cutoutPercentage: 50,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = this.truncate(context.label, 15);
            const value = Math.floor(((context.dataset.data[context.dataIndex] / 100) * 100) + 0.5);
            return label + " (" + value + "%)";
          }
        }
      }
    }
  };

  constructor(
    public as: AppService,
    public ps: PageService,
    private dbs: IrbDashboardService,
    private router: Router,
    public dataTransferService: DataTransferService,
    private ats: AnalyticsService
  ) { }

  ngOnInit() {
    this.ps.getUserDetails().pipe(takeUntil(this.destroyed$)).subscribe((d) => {
      if (d) {
        this.ps.deserializePageState(this.as.getFromSession("pageState"), ["modelGridDiv"]);

        this.dbs.getTopModels().pipe(takeUntil(this.destroyed$)).subscribe((d) => {
          this.data = d as ITopModelDetails;
          this.update();
        });
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  update() {
    const labels: Array<string> = [];
    const percentages: Array<number> = [];
    const t = this;

    this.data.topModels.sort(function (a: any, b: any) {
      return b.aum - a.aum;
    });

    let totalAUM = 0;

    this.data.remainingModels.forEach((item: any) => {
      totalAUM += item.aum;
    });

    this.data.topModels.forEach((item: any) => {
      totalAUM += item.aum;
    });

    this.data.topModels.forEach((item: any) => {
      labels.push(item.modelName);
      percentages.push((item.aum / totalAUM) * 100);
    });

    this.data.remainingModels.forEach((item: any) => {
      labels.push("Other");
      percentages.push((item.aum / totalAUM) * 100);
    });

    this.chartData = {
      labels: labels,
      datasets: [
        {
          data: (totalAUM > 0) ? percentages : [100],
          backgroundColor: (totalAUM > 0) ? this.colors : ["#CECECE"],
          hoverBackgroundColor: (totalAUM > 0) ? this.colors : ["#CECECE"],
          borderWidth: 2,
          hoverBorderColor: ["#FFFFFF"]
        }
      ]
    };

    this.options.plugins.tooltip.enabled = (totalAUM > 0);
  }

  truncate(s: string, n: number) {
    return (s.length > n) ? s.substr(0, n - 1) + '...' : s;
  }

  showModels() {
    this.ats.trackPassiveEvent("lc_topmodels");
    this.router.navigate(['/models/models']);
  }

  showModelDetail(modelId: string, modelName: string) {
    this.ats.trackPassiveEvent("lc_modelname");

    const state: IApplyFilterOnLanding = {
      isUpdatRequired: true,
      openEditModel: true,
      modelId: modelId,
      filterModel: { name: 'name', value: modelName, isUpdatRequired: true }
    } as IApplyFilterOnLanding;

    this.router.navigateByUrl('/models/models', { state: state });
  }

  get data() {
    return this._data;
  }

  set data(d: ITopModelDetails) {
    this._data = d;
  }
}
